import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Form, Input, Button, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { useUser } from '../../user/userContext';
import { useToken } from '../token';
import AuthAPI from '../AuthAPI';
import Logo from '../../components/Logo';
import ResetPasswordModal from '../components/ResetPasswordModal';

type FormValues = {
  email: string;
  password: string;
};

const Login: React.FC = () => {
  const location = useLocation();
  const [, setToken] = useToken();
  const [, setUser] = useUser();
  const [error, setError] = useState<string | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showResetPasswordModal, setShowResetPasswordModal] =
    useState<boolean>(false);

  const onFinish = async (values: FormValues) => {
    setLoading(true);
    try {
      const { data } = await AuthAPI.login({
        email: values.email,
        password: values.password,
      });
      setUser(data.user);
      setToken(data.jwt);
    } catch (err) {
      if (isAxiosError(err)) {
        setError(err?.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-200 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="flex flex-col justify-center mb-4">
          <Logo className="h-12 fill-current text-red-700 stroke-current" />
          <h2 className="mt-6 text-center text-3xl leading-9 font-bold">
            {t('views.Login.loginToYourAccount')}
          </h2>
        </div>

        {location.state?.sessionExpired && (
          <Alert
            type="error"
            message={t('views.Login.sessionExpired')}
            description={t('views.Login.loginAgain')}
            showIcon
            className="mb-4"
          />
        )}

        <div className="bg-white p-10 shadow-md rounded-md">
          <Form
            onFinish={(values) => onFinish(values as FormValues)}
            onValuesChange={() => setError(null)}
            layout="vertical">
            <Form.Item label={t('common.email')} name="email">
              <Input type="email" size="large" autoComplete="email" />
            </Form.Item>
            <Form.Item label={t('views.Login.password')} name="password">
              <Input type="password" size="large" />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loading}
                size="large"
                className="w-full mt-4 font-medium bg-primary-800 border-primary-800 hover:bg-primary-700 hover:border-primary-700 focus:bg-primary-900 focus:border-primary-900">
                {t('views.Login.login')}
              </Button>
            </Form.Item>
            <a onClick={() => setShowResetPasswordModal(true)}>
              {t('views.Login.forgotPassword')}
            </a>
            {error && <Alert message={error} type="error" />}
            <ResetPasswordModal
              open={showResetPasswordModal}
              onCancel={() => setShowResetPasswordModal(false)}
            />
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;
